import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Section, Stack } from "@origin-digital/ods-core";
import { logger } from "@origin-digital/reporting-client";
import { IAppContext } from "../AppContext";
import { ArticleBodyController } from "../components/ArticleBody";
import { routes } from "../helpers/router.helper";
import { IArticleAPIResponse } from "../../../shared/interface/article";
import { UseParamsType } from "../router";
import { useDaxi } from "../services/useDaxi";
import { ContentHeader } from "../components/SearchBanner/ContentHeader";
import { PathnameMap } from "../interfaces/breadcrumbs";
import {
  BreadcrumbBaseUri,
  BreadrumbBaseName,
} from "../components/Breadcrumbs/Breadcrumbs.controller";
import { IBreadcrumb } from "../../../shared/interface/breadcrumb";
import { Loading } from "../components/Loading";
import { getArticleAPIPath } from "../services/paths";
import metaContent from "../cms/meta.json";
import { ScreenTemplate } from "./ScreenTemplate";

interface IProps {
  context: IAppContext;
}

const BreadcrumbMap = (breadcrumbs: IBreadcrumb[]): PathnameMap => {
  const breadcrumbMap = { [BreadcrumbBaseUri]: BreadrumbBaseName };
  if (breadcrumbs && breadcrumbs.length === 3) {
    breadcrumbMap[`${BreadcrumbBaseUri}/${breadcrumbs[0].uri}`] =
      breadcrumbs[0].text;
    breadcrumbMap[
      `${BreadcrumbBaseUri}/${breadcrumbs[0].uri}/${breadcrumbs[1].uri}`
    ] = breadcrumbs[1].text;
    breadcrumbMap[
      `${BreadcrumbBaseUri}/${breadcrumbs[0].uri}/${breadcrumbs[1].uri}/${breadcrumbs[2].uri}`
    ] = breadcrumbs[2].text;
  }
  return breadcrumbMap;
};

export const Article = ({ context }: IProps) => {
  const { category, subcategory, article } = useParams() as UseParamsType;
  const navigate = useNavigate();

  const articleRes = useDaxi<IArticleAPIResponse>(
    getArticleAPIPath(category, subcategory, article),
    {
      xhr: true,
    }
  );
  const daxiError = articleRes.error;
  const data = articleRes.data;
  const articleData = data?.article;

  if (daxiError || (data !== undefined && Object.entries(data).length === 0)) {
    logger.warn(`[Article] cannot find article: "${articleData}"`);
    navigate(routes.subcategory(category, subcategory), {
      replace: true,
    });
  }

  const title = data?.article?.title || "";
  const description = articleRes.loading
    ? ""
    : data?.article?.description || metaContent.defaultDescription;
  const breadcrumbs = data?.breadcrumbs || [];
  const metadata = { title, description };

  return articleData ? (
    <ScreenTemplate data-id="article" metadata={metadata}>
      <Section backgroundColor="transparent">
        <ContentHeader
          title=""
          description=""
          isNative={context.isNative}
          hideTitleAndDescription={true}
          hideBreadcrumbs={false}
          titleIsHeading={true}
          category={category}
          breadcrumbsMap={BreadcrumbMap(breadcrumbs)}
        />
      </Section>
      <Section backgroundColor="cardBackground">
        <Stack space="xlarge">
          <Stack space="medium">
            <ArticleBodyController article={articleData} />
          </Stack>
        </Stack>
      </Section>
    </ScreenTemplate>
  ) : (
    <Loading />
  );
};
